import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'react-toastify';
import './AdminComponents.css';
import { respondToBookingRequest } from '../../services/api'; // Import the API function

const AdminBookingsManagement = () => {
  const { currentUser } = useAuth();
  const [bookingRequests, setBookingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [bookingDocuments, setBookingDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [formData, setFormData] = useState({
    action: '',
    adminNotes: ''
  });
  const [filterStatus, setFilterStatus] = useState('all'); // Changed default to 'all' to show all bookings by default
  const [lastRefresh, setLastRefresh] = useState(null);

  useEffect(() => {
    // Only load if user is a super admin
    if (currentUser && currentUser.role === 'super_admin') {
      loadBookingRequests();
    }
  }, [currentUser, filterStatus]);

  const loadBookingRequests = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      };
      
      // Add timestamp to prevent caching issues
      const timestamp = new Date().getTime();
      setLastRefresh(new Date().toLocaleTimeString());
      
      // Use the admin endpoint directly - this already filters out user requests
      let bookings = [];
      let endpointSuccess = false;
      
      console.log('Fetching admin bookings...');
      
      // Try the admin bookings endpoint first - this should get only admin-relevant bookings
      try {
        const adminEndpoint = `/api/bookings/admin?_t=${timestamp}`;
        console.log(`Trying admin bookings endpoint: ${adminEndpoint}`);
        
        const res = await axios.get(adminEndpoint, config);
        
        if (res.data && Array.isArray(res.data)) {
          console.log(`Retrieved ${res.data.length} bookings from admin endpoint`);
          
          // Filter based on status if needed
          if (filterStatus !== 'all') {
            // Special case for 'sold' - include 'completed' bookings as well
            if (filterStatus === 'sold') {
              bookings = res.data.filter(booking => booking.status === 'sold' || booking.status === 'completed');
              console.log(`Filtered to ${bookings.length} bookings with status sold/completed`);
            } else {
              bookings = res.data.filter(booking => booking.status === filterStatus);
              console.log(`Filtered to ${bookings.length} bookings with status ${filterStatus}`);
            }
          } else {
            // For 'all' filter, use all bookings from admin endpoint (already filtered server-side)
            bookings = res.data;
            console.log(`Using all ${bookings.length} bookings from admin endpoint`);
          }
          
          endpointSuccess = true;
        }
      } catch (adminEndpointError) {
        console.error('Error with admin bookings endpoint:', adminEndpointError);
      }

      // If admin endpoint fails, try the all endpoint as fallback but filter out user requests
      if (!endpointSuccess) {
        try {
          const allEndpoint = `/api/bookings/all?_t=${timestamp}`;
          console.log(`Trying all bookings endpoint as fallback: ${allEndpoint}`);
          
          const res = await axios.get(allEndpoint, config);
          
          if (res.data && Array.isArray(res.data)) {
            console.log(`Retrieved ${res.data.length} bookings from all endpoint`);
            
            // Filter based on status if needed
            if (filterStatus !== 'all') {
              // Special case for 'sold' - include 'completed' bookings as well
              if (filterStatus === 'sold') {
                bookings = res.data.filter(booking => 
                  (booking.status === 'sold' || booking.status === 'completed') &&
                  booking.agentAdmin // Only agent bookings, not user requests
                );
                console.log(`Filtered to ${bookings.length} agent bookings with status sold/completed`);
              } else {
                bookings = res.data.filter(booking => 
                  booking.status === filterStatus &&
                  booking.agentAdmin // Only agent bookings, not user requests
                );
                console.log(`Filtered to ${bookings.length} agent bookings with status ${filterStatus}`);
              }
            } else {
              // For 'all' filter, only include admin-relevant statuses and exclude user requests
              // NEVER include pending_agent (those are user requests for agent dashboard only)
              bookings = res.data.filter(booking => 
                booking.agentAdmin && // Only agent bookings, not user requests
                (booking.status === 'pending_admin' || 
                 booking.status === 'approved' || 
                 booking.status === 'rejected' ||
                 booking.status === 'sold' ||
                 booking.status === 'completed')
              );
              console.log(`Filtered to ${bookings.length} agent bookings with admin-relevant statuses`);
            }
            
            endpointSuccess = true;
          }
        } catch (allEndpointError) {
          console.error('Error with all bookings endpoint:', allEndpointError);
        }
      }
      
      // Set the bookings data
      if (endpointSuccess) {
        console.log(`Setting ${bookings.length} bookings in state`);
        // Log the first booking for debugging
        if (bookings.length > 0) {
          console.log('First booking details:', {
            id: bookings[0]._id,
            status: bookings[0].status,
            agentAdmin: bookings[0].agentAdmin ? 'YES' : 'NO',
            createdAt: bookings[0].createdAt
          });
        }
        setBookingRequests(bookings);
      } else {
        console.warn('All API calls failed');
        setError('Failed to load booking requests. Please try refreshing the page.');
        setBookingRequests([]);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching booking requests:', err);
      setError('Failed to load booking requests');
      setLoading(false);
    }
  };

  const handleAction = (request) => {
    // Only allow processing of all statuses except 'sold', 'completed', and 'rejected'
    if (request.status === 'sold' || request.status === 'completed' || request.status === 'rejected') {
      toast.warning(`You cannot process bookings that are already ${request.status}`);
      return;
    }
    
    setSelectedRequest(request);
    setFormData({
      action: '',
      adminNotes: ''
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFilterChange = (status) => {
    setFilterStatus(status);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.action) {
      toast.error('Please select an action (booked, sold, or reject)');
      return;
    }
    
    try {
      setLoading(true);
      
      // Map the action to a valid status value accepted by the server
      let status;
      if (formData.action === 'reject') {
        status = 'rejected';
      } else if (formData.action === 'booked') {
        status = 'approved'; // Change 'booked' to 'approved' as per server schema
      } else if (formData.action === 'sold') {
        status = 'sold'; // Assuming 'sold' is a valid status
      } else {
        status = formData.action;
      }
      
      // Log the request for debugging
      console.log(`Attempting to update booking ${selectedRequest._id} with data:`, { status, message: formData.adminNotes });
      
      // Use the respondToBookingRequest function from the API service which has fallback mechanisms
      try {
        const response = await respondToBookingRequest(
          selectedRequest._id,
          status,
          formData.adminNotes
        );
        
        console.log('Status update successful:', response);
        
        // Show success message based on action
        if (formData.action === 'booked') {
          toast.success('✅ Booking confirmed and marked as booked. Lot status updated.');
        } else if (formData.action === 'sold') {
          toast.success('✅ Booking marked as sold. Lot status updated.');
        } else {
          toast.success('✅ Booking rejected. Lot status returned to available.');
        }
        
        // Reset form and reload requests
        setSelectedRequest(null);
        setFormData({ action: '', adminNotes: '' });
        
        // Force refresh with cache busting and immediate update
        setTimeout(() => {
          // Clear any cached data
          setBookingRequests([]);
          setLoading(true);
          loadBookingRequests();
        }, 500);
      } catch (err) {
        console.error('Error updating booking status:', err);
        let errorMessage = 'Failed to process request';
        
        if (err.response && err.response.data) {
          if (err.response.data.message) {
            errorMessage = err.response.data.message;
          }
        }
        
        toast.error(errorMessage);
      }
    } catch (err) {
      console.error('Error processing booking request:', err);
      toast.error('Failed to process booking request');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    // Add debugging to see what status we're actually receiving
    console.log('getStatusBadge called with status:', status, typeof status);
    
    switch(status) {
      case 'pending_admin':
        return <span className="badge pending">Pending Your Approval</span>;
      case 'booked':
        return <span className="badge booked">Booked</span>;
      case 'approved':
        return <span className="badge booked">Booked</span>;
      case 'sold':
        return <span className="badge sold">Sold</span>;
      case 'completed':
        return <span className="badge sold">Sold</span>;
      case 'rejected':
        return <span className="badge rejected">Rejected</span>;
      default:
        console.warn('Unknown status received:', status);
        return <span className="badge">{status}</span>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleRefresh = () => {
    loadBookingRequests();
    toast.info('Refreshing booking data...');
  };

  // Get agency name from booking request
  const getAgencyName = (request) => {
    // First check if agency is an object with name property
    if (request.agency && typeof request.agency === 'object' && request.agency.name) {
      return request.agency.name;
    }
    
    // Then check if agency is a string (name or ID)
    if (request.agency && typeof request.agency === 'string') {
      return request.agency;
    }
    
    // Check for agencyId (might be ID reference or populated object)
    if (request.agencyId) {
      if (typeof request.agencyId === 'object') {
        return request.agencyId.name || `Agency ID: ${request.agencyId._id}`;
      } else if (typeof request.agencyId === 'string') {
        return `Agency ID: ${request.agencyId.substring(0, 6)}...`;
      }
      return `Agency ID: ${request.agencyId}`;
    }
    
    // Check agent admin info
    if (request.agentAdmin) {
      if (typeof request.agentAdmin === 'object') {
        return `Agent: ${request.agentAdmin.name || request.agentAdmin.email || 'Unknown'}`;
      }
      return `Agent: ${typeof request.agentAdmin === 'string' ? request.agentAdmin.substring(0, 6) + '...' : 'Unknown'}`;
    }
    
    // Check user's agency if available
    if (request.user && request.user.agencyId) {
      if (typeof request.user.agencyId === 'object') {
        return `User Agency: ${request.user.agencyId.name || request.user.agencyId._id}`;
      }
      return `User Agency: ${request.user.agencyId}`;
    }
    
    // Default fallback
    return 'Direct Booking';
  };

  // New function to handle viewing details
  const handleViewDetails = async (request) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
    
    // Load documents for this booking
    loadBookingDocuments(request._id);
    
    // Parse agent information from requestMessage if available
    if (request.requestMessage && request.requestMessage.includes('Agent:')) {
      console.log('Booking contains agent information:', request.requestMessage);
    }
  };
  
  // Function to load documents related to a booking
  const loadBookingDocuments = async (bookingId) => {
    try {
      setLoadingDocuments(true);
      
      // Get the booking details to check for document URLs
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
      
      // First try to get documents from the dedicated API endpoint
      try {
        const docsResponse = await axios.get(`/api/uploads/documents/${bookingId}`, config);
        
        if (docsResponse.data && Array.isArray(docsResponse.data) && docsResponse.data.length > 0) {
          console.log('Successfully loaded documents from API:', docsResponse.data);
          
          // Use the list of documents from the API
          setBookingDocuments(docsResponse.data);
          return;
        } else {
          console.log('No documents found from API endpoint for booking:', bookingId);
        }
      } catch (apiErr) {
        console.error('Error loading documents from API:', apiErr);
        // Continue to fallback method if API fails
      }
      
      // Fallback: Get the booking details and extract document info manually
      const response = await axios.get(`/api/bookings/${bookingId}`, config);
      const booking = response.data;
      
      console.log('Checking for documents in booking object:', booking);
      
      // Collect all document types from the booking
      const documents = [];
      const baseUrl = window.location.origin;
      
      // Helper function to format URL
      const formatUrl = (url) => {
        if (!url) return null;
        return url.startsWith('http') 
          ? url 
          : `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
      };
      
      // Check for standard document
      if (booking.documentUrl) {
        console.log('Standard document found:', booking.documentUrl);
        documents.push({
          documentUrl: formatUrl(booking.documentUrl),
          documentType: 'uploaded_document',
          documentName: booking.documentOriginalName || 'Uploaded Document'
        });
      }
      
      // Check for cheque document
      if (booking.chequeUrl) {
        console.log('Cheque document found:', booking.chequeUrl);
        documents.push({
          documentUrl: formatUrl(booking.chequeUrl),
          documentType: 'cheque',
          documentName: booking.chequeOriginalName || 'Payment Cheque'
        });
      }
      
      // Check for IC front document
      if (booking.icFrontUrl) {
        console.log('IC Front document found:', booking.icFrontUrl);
        documents.push({
          documentUrl: formatUrl(booking.icFrontUrl),
          documentType: 'ic_front',
          documentName: booking.icFrontOriginalName || 'IC Front'
        });
      }
      
      // Check for IC back document
      if (booking.icBackUrl) {
        console.log('IC Back document found:', booking.icBackUrl);
        documents.push({
          documentUrl: formatUrl(booking.icBackUrl),
          documentType: 'ic_back',
          documentName: booking.icBackOriginalName || 'IC Back'
        });
      }
      
      console.log('Final document list:', documents);
      setBookingDocuments(documents);
    } catch (err) {
      console.error('Error loading booking documents:', err);
      setBookingDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  };
  
  // Close the details modal
  const handleCloseDetails = () => {
    setShowDetailsModal(false);
    setSelectedRequest(null);
    setBookingDocuments([]);
    setFormData({ action: '', adminNotes: '' });
  };
  
  // Safely extract information from booking request message
  const extractAgentInfo = (requestMessage) => {
    if (!requestMessage) return {};
    
    const info = {};
    
    // Try to extract "Agent:" line
    const agentMatch = requestMessage.match(/Agent:\s*([^\n]+)/);
    if (agentMatch && agentMatch[1]) {
      info.agentName = agentMatch[1].trim();
    }
    
    // Try to extract "Phone:" line
    const phoneMatch = requestMessage.match(/Phone:\s*([^\n]+)/);
    if (phoneMatch && phoneMatch[1]) {
      info.phone = phoneMatch[1].trim();
    }
    
    // Try to extract "Email:" line
    const emailMatch = requestMessage.match(/Email:\s*([^\n]+)/);
    if (emailMatch && emailMatch[1]) {
      info.email = emailMatch[1].trim();
    }
    
    // Try to extract "Description:" section (everything after)
    const descMatch = requestMessage.match(/Description:\s*([\s\S]+)$/);
    if (descMatch && descMatch[1]) {
      info.description = descMatch[1].trim();
    }
    
    return info;
  };
  
  // Function to view document (open in new tab)
  const viewDocument = (documentUrl) => {
    if (!documentUrl) {
      console.error('No document URL provided');
      return;
    }
    
    // Ensure the URL is properly formatted
    let formattedUrl = documentUrl;
    
    // Handle both relative and absolute URLs
    if (!documentUrl.startsWith('http')) {
      // For relative URLs, prepend the origin
      // Remove any leading slashes to avoid double slashes
      const cleanUrl = documentUrl.replace(/^\/+/, '');
      formattedUrl = `${window.location.origin}/${cleanUrl}`;
    }
    
    console.log('Opening document URL:', formattedUrl);
    window.open(formattedUrl, '_blank');
  };

  // Function to download document
  const downloadDocument = (documentUrl, documentName) => {
    if (!documentUrl) {
      console.error('No document URL provided');
      return;
    }
    
    // Ensure the URL is properly formatted
    let formattedUrl = documentUrl;
    
    // Handle both relative and absolute URLs
    if (!documentUrl.startsWith('http')) {
      // For relative URLs, prepend the origin
      // Remove any leading slashes to avoid double slashes
      const cleanUrl = documentUrl.replace(/^\/+/, '');
      formattedUrl = `${window.location.origin}/${cleanUrl}`;
    }
    
    console.log('Downloading document from URL:', formattedUrl);
    
    // Create a temporary link element and trigger download
    const link = document.createElement('a');
    link.href = formattedUrl;
    link.download = documentName || 'document';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading && !error) {
    return <div className="loading">Loading booking requests...</div>;
  }

  return (
    <div className="admin-component">
      <h2>Booking Requests Management</h2>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="controls-row">
        {/* Status filter buttons */}
        <div className="filter-buttons">
          <button 
            className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
            onClick={() => handleFilterChange('all')}
          >
            All Requests
          </button>
          <button 
            className={`filter-btn ${filterStatus === 'pending_admin' ? 'active' : ''}`}
            onClick={() => handleFilterChange('pending_admin')}
          >
            Pending
          </button>
          <button 
            className={`filter-btn ${filterStatus === 'approved' ? 'active' : ''}`}
            onClick={() => handleFilterChange('approved')}
          >
            Approved
          </button>
          <button 
            className={`filter-btn ${filterStatus === 'sold' ? 'active' : ''}`}
            onClick={() => handleFilterChange('sold')}
          >
            Sold
          </button>
          <button 
            className={`filter-btn ${filterStatus === 'rejected' ? 'active' : ''}`}
            onClick={() => handleFilterChange('rejected')}
          >
            Rejected
          </button>
        </div>
        
        <button 
          className="refresh-btn"
          onClick={handleRefresh}
          disabled={loading}
        >
          Refresh Data
        </button>
      </div>
      
      {lastRefresh && (
        <div className="last-refresh">
          Last refreshed: {lastRefresh}
        </div>
      )}
      
      {bookingRequests.length === 0 ? (
        <div className="no-data">
          No booking requests found for the selected filter.
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Booking ID</th>
                <th>Unit/Lot</th>
                <th>Agency</th>
                <th>Date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {bookingRequests.map((request) => (
                <tr key={request._id} className={request.status === 'pending_admin' ? 'highlight-row' : ''}>
                  <td>{request._id.substring(request._id.length - 8)}</td>
                  <td>
                    {request.unitId || request.unit ? (
                      <>
                        {request.unit?.lotNumber || request.unit?.unitNumber || 'Unknown Unit'} 
                        <span className="property-type">(Unit)</span>
                      </>
                    ) : (
                      <>
                        {request.lot?.lotNumber || request.specialLotNumber || 'Unknown Lot'} 
                        <span className="property-type">
                          {request.specialLotType || (request.lot?.type ? `Type ${request.lot.type}` : 'Lot')}
                        </span>
                      </>
                    )}
                  </td>
                  <td>{getAgencyName(request)}</td>
                  <td>{formatDate(request.createdAt)}</td>
                  <td>{getStatusBadge(request.status)}</td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="view-btn"
                        onClick={() => handleViewDetails(request)}
                      >
                        View Details
                      </button>
                      
                      {request.status !== 'sold' && request.status !== 'completed' && request.status !== 'rejected' && (
                        <button 
                          className="action-btn"
                          onClick={() => handleAction(request)}
                        >
                          Process
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Booking Details Modal */}
      {showDetailsModal && selectedRequest && (
        <div className="modal-overlay">
          <div className="modal-content details-modal">
            <div className="modal-header">
              <h3>Booking Details</h3>
              <button className="close-btn" onClick={handleCloseDetails}>&times;</button>
            </div>
            
            <div className="detail-content">
              <div className="detail-section">
                <h4>Property Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Property Type:</span>
                    <span className="detail-value">
                      {selectedRequest.specialLotType || 
                       selectedRequest.lot?.type || 
                       selectedRequest.unit?.type || 'N/A'}
                    </span>
                  </div>
                  
                  <div className="detail-item">
                    <span className="detail-label">Lot/Unit Number:</span>
                    <span className="detail-value">
                      {selectedRequest.specialLotNumber || 
                       selectedRequest.lot?.lotNumber || 
                       selectedRequest.unit?.lotNumber || 
                       selectedRequest.unit?.unitNumber || 'N/A'}
                    </span>
                  </div>
                  
                  {(selectedRequest.unit?.buildUp || selectedRequest.lot?.buildUp) && (
                    <div className="detail-item">
                      <span className="detail-label">Build Up Area:</span>
                      <span className="detail-value">
                        {selectedRequest.unit?.buildUp || selectedRequest.lot?.buildUp} sqft
                      </span>
                    </div>
                  )}
                  
                  {(selectedRequest.unit?.price || selectedRequest.lot?.price) && (
                    <div className="detail-item">
                      <span className="detail-label">Price:</span>
                      <span className="detail-value">
                        {selectedRequest.unit?.price || selectedRequest.lot?.price}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="detail-section">
                <h4>Agent Information</h4>
                <div className="detail-grid">
                  {/* Attempt to extract agent info from message if available */}
                  {selectedRequest.requestMessage && (
                    <>
                      {(() => {
                        const agentInfo = extractAgentInfo(selectedRequest.requestMessage);
                        return (
                          <>
                            {agentInfo.agentName && (
                              <div className="detail-item">
                                <span className="detail-label">Agent Name:</span>
                                <span className="detail-value">{agentInfo.agentName}</span>
                              </div>
                            )}
                            
                            {agentInfo.phone && (
                              <div className="detail-item">
                                <span className="detail-label">Phone:</span>
                                <span className="detail-value">{agentInfo.phone}</span>
                              </div>
                            )}
                            
                            {agentInfo.email && (
                              <div className="detail-item">
                                <span className="detail-label">Email:</span>
                                <span className="detail-value">{agentInfo.email}</span>
                              </div>
                            )}
                            
                            {agentInfo.description && (
                              <div className="detail-item full-width">
                                <span className="detail-label">Description:</span>
                                <span className="detail-value">{agentInfo.description}</span>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </>
                  )}
                  
                  {/* Fallback to standard booking fields if no agent info extracted */}
                  {(!selectedRequest.requestMessage || !extractAgentInfo(selectedRequest.requestMessage).agentName) && (
                    <>
                      <div className="detail-item">
                        <span className="detail-label">Requester Name:</span>
                        <span className="detail-value">
                          {selectedRequest.creatorName || 
                           (selectedRequest.user && selectedRequest.user.name) || 'N/A'}
                        </span>
                      </div>
                      
                      <div className="detail-item">
                        <span className="detail-label">Contact Phone:</span>
                        <span className="detail-value">
                          {selectedRequest.creatorPhone || 
                           selectedRequest.contactPhone || 
                           (selectedRequest.user && selectedRequest.user.phone) || 'N/A'}
                        </span>
                      </div>
                      
                      <div className="detail-item">
                        <span className="detail-label">Email:</span>
                        <span className="detail-value">
                          {selectedRequest.creatorEmail || 
                           (selectedRequest.user && selectedRequest.user.email) || 'N/A'}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
              
              {/* Show booking request message if it exists */}
              {selectedRequest.requestMessage && !extractAgentInfo(selectedRequest.requestMessage).agentName && (
                <div className="detail-section">
                  <h4>Request Message</h4>
                  <div className="request-message">
                    {selectedRequest.requestMessage}
                  </div>
                </div>
              )}
              
              {/* Documents Section */}
              <div className="detail-section">
                <h4>Uploaded Documents</h4>
                {loadingDocuments ? (
                  <div className="loading-indicator">Loading documents...</div>
                ) : bookingDocuments.length > 0 ? (
                  <div className="documents-list">
                    {/* Group documents by type */}
                    {bookingDocuments.some(doc => doc.documentType === 'cheque') && (
                      <div className="document-group">
                        <h5>Payment Cheque</h5>
                        {bookingDocuments
                          .filter(doc => doc.documentType === 'cheque')
                          .map((doc, index) => (
                            <div key={`cheque-${index}`} className="document-item">
                              <span className="document-name">{doc.documentName}</span>
                              <div className="document-actions">
                                <button 
                                  className="view-document-btn"
                                  onClick={() => viewDocument(doc.documentUrl)}
                                >
                                  View
                                </button>
                                <button 
                                  className="download-document-btn"
                                  onClick={() => downloadDocument(doc.documentUrl, doc.documentName)}
                                >
                                  Download
                                </button>
                              </div>
                            </div>
                          ))
                        }
                      </div>
                    )}
                    
                    {bookingDocuments.some(doc => doc.documentType === 'ic_front' || doc.documentType === 'ic_back') && (
                      <div className="document-group">
                        <h5>Identity Card</h5>
                        {bookingDocuments
                          .filter(doc => doc.documentType === 'ic_front' || doc.documentType === 'ic_back')
                          .map((doc, index) => (
                            <div key={`ic-${index}`} className="document-item">
                              <span className="document-type">
                                {doc.documentType === 'ic_front' ? 'Front' : 'Back'}
                              </span>
                              <span className="document-name">{doc.documentName}</span>
                              <div className="document-actions">
                                <button 
                                  className="view-document-btn"
                                  onClick={() => viewDocument(doc.documentUrl)}
                                >
                                  View
                                </button>
                                <button 
                                  className="download-document-btn"
                                  onClick={() => downloadDocument(doc.documentUrl, doc.documentName)}
                                >
                                  Download
                                </button>
                              </div>
                            </div>
                          ))
                        }
                      </div>
                    )}
                    
                    {/* Other documents */}
                    {bookingDocuments.some(doc => doc.documentType !== 'cheque' && doc.documentType !== 'ic_front' && doc.documentType !== 'ic_back') && (
                      <div className="document-group">
                        <h5>Other Documents</h5>
                        {bookingDocuments
                          .filter(doc => doc.documentType !== 'cheque' && doc.documentType !== 'ic_front' && doc.documentType !== 'ic_back')
                          .map((doc, index) => (
                            <div key={`other-${index}`} className="document-item">
                              <span className="document-name">{doc.documentName}</span>
                              <div className="document-actions">
                                <button 
                                  className="view-document-btn"
                                  onClick={() => viewDocument(doc.documentUrl)}
                                >
                                  View
                                </button>
                                <button 
                                  className="download-document-btn"
                                  onClick={() => downloadDocument(doc.documentUrl, doc.documentName)}
                                >
                                  Download
                                </button>
                              </div>
                            </div>
                          ))
                        }
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="no-documents">No documents uploaded with this booking.</div>
                )}
              </div>
              
              <div className="detail-section">
                <h4>Booking Status</h4>
                <div className="status-info">
                  <div className="status-badge">{getStatusBadge(selectedRequest.status)}</div>
                  <div className="booking-dates">
                    <div><strong>Created:</strong> {formatDate(selectedRequest.createdAt)}</div>
                    {selectedRequest.updatedAt && (
                      <div><strong>Last Updated:</strong> {formatDate(selectedRequest.updatedAt)}</div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="modal-footer">
                <button 
                  className="close-details-btn"
                  onClick={handleCloseDetails}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Existing Processing Modal */}
      {selectedRequest && !showDetailsModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Process Booking Request</h3>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Action</label>
                <div className="radio-group">
                  <label>
                    <input
                      type="radio"
                      name="action"
                      value="booked"
                      checked={formData.action === 'booked'}
                      onChange={handleChange}
                    />
                    Booked (Payment Received)
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="action"
                      value="sold"
                      checked={formData.action === 'sold'}
                      onChange={handleChange}
                    />
                    Sold (Sale Finalized)
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="action"
                      value="reject"
                      checked={formData.action === 'reject'}
                      onChange={handleChange}
                    />
                    Reject (Back to Available)
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="adminNotes">Notes (optional)</label>
                <textarea
                  id="adminNotes"
                  name="adminNotes"
                  value={formData.adminNotes}
                  onChange={handleChange}
                  placeholder="Add your notes here..."
                  rows="3"
                />
              </div>
              
              <div className="form-actions">
                <button 
                  type="button" 
                  className="cancel-btn"
                  onClick={() => {
                    setSelectedRequest(null);
                    setShowDetailsModal(false);
                    setFormData({ action: '', adminNotes: '' });
                    setBookingDocuments([]);
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={!formData.action}
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBookingsManagement; 